/**
 * Collection of tips and facts to display during story generation
 */

export interface StoryTip {
  id: number;
  text: string;
  category: 'fact' | 'tip' | 'app' | 'child' | 'bedtime';
}

// Array of story tips and facts
export const storyTips: StoryTip[] = [
  // Facts about storytelling
  {
    id: 1,
    text: "The human brain is wired to remember stories better than plain facts. Stories engage multiple areas of the brain simultaneously.",
    category: "fact"
  },
  {
    id: 2,
    text: "Stories have been used to pass down knowledge for over 27,000 years, dating back to cave paintings.",
    category: "fact"
  },
  {
    id: 3,
    text: "Reading fiction has been shown to improve empathy by helping people understand different perspectives.",
    category: "fact"
  },
  {
    id: 4,
    text: "Children who are read to regularly have stronger language skills and larger vocabularies.",
    category: "fact"
  },
  {
    id: 5,
    text: "Our AI creates unique stories every time - no two stories are ever the same!",
    category: "app"
  },
  
  // Tips for parents
  {
    id: 6,
    text: "Using different voices for characters can make the story more engaging for children.",
    category: "tip"
  },
  {
    id: 7,
    text: "Asking your child questions about the story helps develop their critical thinking skills.",
    category: "tip"
  },
  {
    id: 8,
    text: "You can save favorite characters and settings to reuse them in future stories.",
    category: "app"
  },
  {
    id: 9,
    text: "Consistent bedtime routines, including storytelling, help children fall asleep more easily.",
    category: "bedtime"
  },
  {
    id: 10,
    text: "Even after children learn to read, they benefit from being read to by adults.",
    category: "child"
  },
  
  // More facts
  {
    id: 11,
    text: "Stories activate parts of the brain that process sights, sounds, smells, and movement.",
    category: "fact"
  },
  {
    id: 12,
    text: "A bedtime story creates a special bonding moment between you and your child.",
    category: "bedtime"
  },
  {
    id: 13,
    text: "The voice options (Friendly, Excited, Calm, Mysterious) each have unique characteristics perfect for different stories.",
    category: "app"
  },
  {
    id: 14,
    text: "Reading just one bedtime story per day exposes a child to about 78,000 words per year!",
    category: "child"
  },
  {
    id: 15,
    text: "Children who read or are read to for fun are more likely to have better test scores than those who don't.",
    category: "fact"
  },
  {
    id: 16,
    text: "You can create stories with multiple characters for more complex and exciting adventures.",
    category: "app"
  },
  {
    id: 17,
    text: "Stories with magical elements help foster creativity and imagination in children.",
    category: "child"
  },
  {
    id: 18,
    text: "Try different story lengths based on your bedtime schedule - short stories are perfect for busy nights!",
    category: "bedtime"
  },
  {
    id: 19,
    text: "Stories that include problem-solving help children develop critical thinking skills.",
    category: "fact"
  },
  {
    id: 20,
    text: "The background sounds can help create a relaxing environment for bedtime.",
    category: "app"
  }
];

/**
 * Get a random tip from the full collection
 */
export const getRandomTip = (): StoryTip => {
  const randomIndex = Math.floor(Math.random() * storyTips.length);
  return storyTips[randomIndex];
};

/**
 * Get a random tip from a specific category
 */
export const getRandomTipByCategory = (category: StoryTip['category']): StoryTip => {
  const filteredTips = storyTips.filter(tip => tip.category === category);
  
  if (filteredTips.length === 0) {
    return getRandomTip();
  }
  
  const randomIndex = Math.floor(Math.random() * filteredTips.length);
  return filteredTips[randomIndex];
};

/**
 * Get a list of randomized tips
 */
export const getRandomTips = (count: number = 5): StoryTip[] => {
  // Make a copy to avoid modifying the original array
  const tipsCopy = [...storyTips];
  const randomizedTips: StoryTip[] = [];
  
  // Shuffle the array using Fisher-Yates algorithm
  for (let i = tipsCopy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [tipsCopy[i], tipsCopy[j]] = [tipsCopy[j], tipsCopy[i]];
  }
  
  // Get the first 'count' items
  for (let i = 0; i < Math.min(count, tipsCopy.length); i++) {
    randomizedTips.push(tipsCopy[i]);
  }
  
  return randomizedTips;
}; 